/* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- Synchronize task progress with external notification and browser-title APIs. */
import { useEffect, useRef } from "react";
import { askForBrowserNotificationPermission, showNotification, showTaskCompletedAnimation, updateBrowserTitleStatus } from "./notifications";

export interface LongRunningTaskNotificationOptions {
  /** Identifies the task being observed; change this when navigating to another task. */
  id: string;
  /** Undefined while loading. Only tasks first observed as running are watched. */
  status: "running" | "completed" | "failed" | undefined;
  permissionReason: string;
  successMessage: string;
  errorMessage: string;
}

interface Observation {
  id: string;
  initialized: boolean;
  watching: boolean;
  notified: boolean;
}

/** Notifies once when a watched task finishes and celebrates successful completion. */
export const useLongRunningTaskNotification = ({ id, status, permissionReason, successMessage, errorMessage }: LongRunningTaskNotificationOptions): void => {
  const observation = useRef<Observation | undefined>(undefined);

  useEffect(() => {
    if (observation.current?.id !== id) {
      observation.current = { id, initialized: false, watching: false, notified: false };
    }
    const current = observation.current;
    if (status === undefined) return;

    if (!current.initialized) {
      current.initialized = true;
      current.watching = status === "running";
      if (current.watching) {
        askForBrowserNotificationPermission(permissionReason);
        updateBrowserTitleStatus("running");
      }
    }

    if (!current.watching || current.notified || status === "running") return;

    current.notified = true;
    const failed = status === "failed";
    const text = failed ? errorMessage : successMessage;
    updateBrowserTitleStatus(status);
    showNotification({
      inAppText: text,
      browserNotificationText: text,
      inAppExpiry: "long",
      status: failed ? "error" : "success",
    });
    if (!failed) showTaskCompletedAnimation();
  }, [id, status, permissionReason, successMessage, errorMessage]);
};
